import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import useSideDetail from '#/hooks/useSideDetail';
import { createAsyncFilterLabelCache } from '#/modules/commonComponents/table/asyncFilterLabelCache';
import ListAddButton from '#/modules/commonComponents/table/ListAddButton';
import ListTablePanel from '#/modules/commonComponents/table/ListTablePanel';
import { buildFilterStatusFieldsFromColumns } from '#/modules/commonComponents/table/buildFilterStatusFields';
import { DEFAULT_LIST_SORT_FIELD_LABELS } from '#/modules/commonComponents/table/listFilterKeys';
import { useCan } from '#/modules/infra/auth/useCan';
import PageShell from '#/modules/infra/layout/PageShell';
import SideMsgLayout from '#/modules/infra/layout/SideMsgLayout';
import StockTransferSideMsg from '#/modules/imsMgmt/receipts/stockTransfer/components/StockTransferSideMsg';
import { buildStockTransferColumns } from '#/modules/imsMgmt/receipts/stockTransfer/data/stockTransferColumns';
import { getStockTransferApiErrorMessage } from '#/modules/imsMgmt/receipts/stockTransfer/helper/apiError';
import { applyTransferDateRangeToFilters } from '#/modules/imsMgmt/receipts/stockTransfer/helper/transferDateListFilter';
import { hydrateWarehouseFilterLabels } from '#/modules/imsMgmt/settings/storePlace/helper/warehouseSelectOptions';
import WorkOrderStockOpsPageHeader from '#/modules/momMgmt/workOrder/components/WorkOrderStockOpsPageHeader';
import useWorkOrderStockTransferList from '#/modules/momMgmt/workOrder/transfer/hooks/useWorkOrderStockTransferList';

const WORK_ORDER_STOCK_TRANSFER_SORT_FIELD_LABELS = {
  ...DEFAULT_LIST_SORT_FIELD_LABELS,
  transferNo: '轉倉單號',
  transferDate: '轉倉日',
  status: '狀態',
};

const WorkOrderStockTransferListPage = () => {
  const listRef = useRef(null);
  const [toWarehouseLabelCache] = useState(createAsyncFilterLabelCache);
  const [labelCacheVersion, setLabelCacheVersion] = useState(0);
  const { displayData, setSelectedData } = useSideDetail();
  const canCreate = useCan(PERM.STOCK_TRANSFER_CREATE);
  const canUpdate = useCan(PERM.STOCK_TRANSFER_UPDATE);

  const {
    rows,
    pagination,
    isLoading,
    isError,
    error,
    refetch,
    listQuery,
    setPage,
    setPageSize,
    setKeyword,
    setFilter,
    setSort,
    removeSortAt,
    resetListQuery,
    setListQuery,
  } = useWorkOrderStockTransferList();

  const setTransferDateRangeFilter = useCallback((range) => {
    setListQuery((prev) => ({
      ...prev,
      page: 1,
      filters: applyTransferDateRangeToFilters(prev.filters, range),
    }));
  }, [setListQuery]);

  const handleFilterChange = useCallback((key, value) => {
    if (key === 'transferDateFrom' && value === undefined) {
      setTransferDateRangeFilter(undefined);
      return;
    }
    setFilter(key, value);
  }, [setFilter, setTransferDateRangeFilter]);

  useEffect(() => {
    let cancelled = false;

    hydrateWarehouseFilterLabels(
      listQuery.filters?.toWarehouseId,
      toWarehouseLabelCache,
    ).then((didHydrate) => {
      if (!cancelled && didHydrate) {
        setLabelCacheVersion((version) => version + 1);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [listQuery.filters?.toWarehouseId, toWarehouseLabelCache]);

  const columns = useMemo(
    () => {
      void labelCacheVersion;
      return buildStockTransferColumns({
        filters: listQuery.filters || {},
        onFilterChange: handleFilterChange,
        onTransferDateRangeChange: setTransferDateRangeFilter,
        includeNavLinks: true,
        canShowEdit: (row) => canUpdate && row.status === 'DRAFT',
        includeSourceTypeFilter: false,
        getDetailPath: (row) => ROUTES.workOrderTransferDetail(row.id),
        getEditPath: (row) => ROUTES.workOrderTransferEdit(row.id),
        toWarehouseLabelCache,
      });
    },
    [
      listQuery.filters,
      handleFilterChange,
      setTransferDateRangeFilter,
      toWarehouseLabelCache,
      labelCacheVersion,
      canUpdate,
    ],
  );

  const filterStatusFields = useMemo(
    () => buildFilterStatusFieldsFromColumns(columns, { filters: listQuery.filters }),
    [columns, listQuery.filters],
  );

  const handleSortingChange = useCallback((sorting) => {
    setSort((sorting ?? []).map((item) => ({
      id: item.id,
      desc: Boolean(item.desc),
    })));
  }, [setSort]);

  const handleRowClick = useCallback((row, isSelected) => {
    if (isSelected) {
      setSelectedData(row.original);
      return;
    }

    setSelectedData(null);
  }, [setSelectedData]);

  const headerAction = canCreate ? (
    <ListAddButton to={ROUTES.workOrderTransferCreate} />
  ) : null;

  return (
    <PageShell variant="listWithSide">
      <div className="layout-shell__body flex min-h-0 flex-col">
        <WorkOrderStockOpsPageHeader page="transfer" variant="list" />
        <SideMsgLayout>
          <div className="table-panel min-h-0 xl:h-full">
            <ListTablePanel
              ref={listRef}
              columns={columns}
              rows={rows}
              pagination={pagination}
              isLoading={isLoading}
              isError={isError}
              errorMessage={isError ? getStockTransferApiErrorMessage(error, '載入列表失敗') : undefined}
              onRetry={isError ? () => { refetch(); } : undefined}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              onRowClick={handleRowClick}
              onSearch={setKeyword}
              onReset={resetListQuery}
              initialKeyword={listQuery.keyword}
              searchPlaceholder="搜尋單號、備註、庫存編號或物料"
              headerAction={headerAction}
              listQuery={listQuery}
              filterStatusFields={filterStatusFields}
              sortFieldLabels={WORK_ORDER_STOCK_TRANSFER_SORT_FIELD_LABELS}
              onFilterChange={handleFilterChange}
              onKeywordClear={() => setKeyword('')}
              onSortRemove={removeSortAt}
              onSortingChange={handleSortingChange}
            />
          </div>

          <StockTransferSideMsg
            displayData={displayData}
            setSelectedData={setSelectedData}
          />
        </SideMsgLayout>
      </div>
    </PageShell>
  );
};

export default WorkOrderStockTransferListPage;
